/* eslint-disable @atlaskit/volt-strict-mode/no-multiple-exports */

import type { Node as PMNode, ResolvedPos } from '@atlaskit/editor-prosemirror/model';
import { NodeSelection, TextSelection } from '@atlaskit/editor-prosemirror/state';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';

import { ACTION, ACTION_SUBJECT, ACTION_SUBJECT_ID, EVENT_TYPE } from '../analytics';
import type { EditorAnalyticsAPI } from '../analytics';
import { withAnalytics } from '../editor-analytics';
import { GapCursorSelection } from '../selection';
import type { Command } from '../types';
import { atTheBeginningOfDoc } from './atTheBeginningOfDoc';
import { atTheEndOfDoc } from './atTheEndOfDoc';
import { filter } from './filter';
import { insertNewLine } from './insertNewLine';
import { isEmptyParagraph } from './isEmptyParagraph';
import { isMediaNode } from './isMediaNode';
export type WalkNode = {
	$pos: ResolvedPos;
	foundNode: boolean;
};

/**
 * Walk forwards from a position until we encounter the (inside) start of
 * the next node, or reach the end of the document.
 *
 * @param $startPos Position to start walking from.
 */
export const walkNextNode = ($startPos: ResolvedPos): WalkNode => {
	let $pos = $startPos;

	// invariant 1: don't walk past the end of the document
	// invariant 2: we are at the beginning or
	//              we haven't walked to the start of *any* node
	//              parentOffset includes textOffset.
	while (
		$pos.pos < $pos.doc.nodeSize - 2 &&
		($pos.pos === $startPos.pos || $pos.parentOffset > 0)
	) {
		$pos = $pos.doc.resolve($pos.pos + 1);
	}

	return {
		$pos: $pos,
		foundNode: $pos.pos < $pos.doc.nodeSize - 2,
	};
};

/**
 * Walk backwards from a position until we encounter the (inside) end of
 * the previous node, or reach the start of the document.
 *
 * @param $startPos Position to start walking from.
 */
export const walkPrevNode = ($startPos: ResolvedPos): WalkNode => {
	let $pos = $startPos;

	while (
		$pos.pos > 0 &&
		($pos.pos === $startPos.pos || $pos.parentOffset < $pos.parent.nodeSize - 2)
	) {
		$pos = $pos.doc.resolve($pos.pos - 1);
	}

	return {
		$pos: $pos,
		foundNode: $pos.pos > 0,
	};
};

export const insertNewLineWithAnalytics = (
	editorAnalyticsAPI: EditorAnalyticsAPI | undefined,
): Command =>
	withAnalytics(editorAnalyticsAPI, {
		action: ACTION.INSERTED,
		actionSubject: ACTION_SUBJECT.TEXT,
		actionSubjectId: ACTION_SUBJECT_ID.LINE_BREAK,
		eventType: EVENT_TYPE.TRACK,
	})(insertNewLine());

export const createNewParagraphAbove: Command = (state, dispatch) => {
	const append = false;
	if (!canMoveUp(state) && canCreateParagraphNear(state)) {
		createParagraphNear(append)(state, dispatch);
		return true;
	}

	return false;
};

export const createNewParagraphBelow: Command = (state, dispatch) => {
	const append = true;
	if (!canMoveDown(state) && canCreateParagraphNear(state)) {
		createParagraphNear(append)(state, dispatch);
		return true;
	}

	return false;
};

function canCreateParagraphNear(state: EditorState): boolean {
	const {
		selection: { $from },
	} = state;
	const node = $from.node($from.depth);
	const insideCodeBlock = !!node && node.type === state.schema.nodes.codeBlock;
	const isNodeSelection = state.selection instanceof NodeSelection;
	return $from.depth > 1 || isNodeSelection || insideCodeBlock;
}

export function createParagraphNear(append: boolean = true): Command {
	return function (state, dispatch) {
		const paragraph = state.schema.nodes.paragraph;

		if (!paragraph) {
			return false;
		}

		let insertPos;

		if (state.selection instanceof TextSelection) {
			if (topLevelNodeIsEmptyTextBlock(state)) {
				return false;
			}
			insertPos = getInsertPosFromTextBlock(state, append);
		} else {
			insertPos = getInsertPosFromNonTextBlock(state, append);
		}

		const tr = state.tr.insert(insertPos, paragraph.createAndFill() as PMNode);
		tr.setSelection(TextSelection.create(tr.doc, insertPos + 1));

		if (dispatch) {
			dispatch(tr);
		}

		return true;
	};
}

function getInsertPosFromTextBlock(state: EditorState, append: boolean): number {
	const { $from, $to } = state.selection;
	let pos;
	if (!append) {
		pos = $from.start(0);
	} else {
		pos = $to.end(0);
	}
	return pos;
}

function getInsertPosFromNonTextBlock(state: EditorState, append: boolean): number {
	const { $from, $to } = state.selection;
	const nodeAtSelection =
		state.selection instanceof NodeSelection && state.doc.nodeAt(state.selection.$anchor.pos);
	const isMediaSelection = nodeAtSelection && nodeAtSelection.type.name === 'mediaGroup';

	let pos;
	if (!append) {
		// The start position is different with text block because it starts from 0
		pos = $from.start($from.depth);
		// The depth is different with text block because it starts from 0
		pos = $from.depth > 0 && !isMediaSelection ? pos - 1 : pos;
	} else {
		pos = $to.end($to.depth);
		pos = $to.depth > 0 && !isMediaSelection ? pos + 1 : pos;
	}
	return pos;
}

function topLevelNodeIsEmptyTextBlock(state: EditorState): boolean {
	const topLevelNode = state.selection.$from.node(1);
	return (
		topLevelNode.isTextblock &&
		topLevelNode.type !== state.schema.nodes.codeBlock &&
		topLevelNode.nodeSize === 2
	);
}

function canMoveUp(state: EditorState): boolean {
	const { selection } = state;
	/**
	 * If there's a media element on the selection it will use a gap cursor to move
	 */
	if (selection instanceof NodeSelection && isMediaNode(selection.node)) {
		return true;
	}

	return !atTheBeginningOfDoc(state);
}

function canMoveDown(state: EditorState): boolean {
	const { selection } = state;

	/**
	 * If there's a media element on the selection it will use a gap cursor to move
	 */
	if (selection instanceof NodeSelection && isMediaNode(selection.node)) {
		return true;
	}

	return !atTheEndOfDoc(state);
}

/**
 * If the selection is empty, is inside a paragraph node and `canNextNodeMoveUp` is true then delete current paragraph
 * and move the node below it up. The selection will be retained, to be placed in the moved node.
 *
 * @param canNextNodeMoveUp check if node directly after the selection is able to be brought up to selection
 * @returns PM Command
 */
export const deleteEmptyParagraphAndMoveBlockUp = (
	canNextNodeMoveUp: (nextNode: PMNode) => boolean,
): Command => {
	return (state, dispatch, view) => {
		const {
			selection: {
				$from: { pos, parent },
				$head,
				empty,
			},
			tr,
			doc,
		} = state;
		const { $pos } = walkNextNode($head);
		const nextPMNode = doc.nodeAt($pos.pos - 1);

		if (
			empty &&
			nextPMNode &&
			canNextNodeMoveUp(nextPMNode) &&
			isEmptyParagraph(parent) &&
			view?.endOfTextblock('right')
		) {
			tr.deleteRange(pos - 1, pos + 1);

			if (dispatch) {
				dispatch(tr);
			}
			return true;
		}

		return false;
	};
};

/**
 * Check if the selection is empty and at the start of a task or list item
 * @param state Editor state
 * @returns true if selection is empty and at the start of a task or list item
 */
export const isEmptySelectionAtStart = (state: EditorState): boolean => {
	const { blockTaskItem } = state.schema.nodes;
	const { empty, $from } = state.selection;

	// If blockTaskItem is in the schema,
	// we need to check if the selection is inside a blockTaskItem
	if (blockTaskItem && empty) {
		// If the parent is in a textblock,
		// check if it's nested inside a blockTaskItem
		if ($from.parent.isTextblock) {
			const $posAtStartOfTextBlock = state.doc.resolve($from.start($from.depth - 1));
			const parentOfTextBlock = $posAtStartOfTextBlock.parent;

			// If the parent of the textblock is a blockTaskItem,
			// we need to if the textblock is the first node in the blockTaskItem
			if (parentOfTextBlock.type === blockTaskItem) {
				const firstChildOfBlockTaskItem = parentOfTextBlock.firstChild;
				if (firstChildOfBlockTaskItem === $from.parent) {
					// If the textblock is the first node in the blockTaskItem,
					// check if the selection is at the start of the textblock
					const firstTextPosInTextBlock = $from.start($from.depth);
					return $from.pos === firstTextPosInTextBlock;
				} else {
					return false;
				}
			}
		}
		// Else, check if the parent is a blockTaskItem
		else if ($from.parent.type === blockTaskItem) {
			// Check if the selection is at the start of the blockTaskItem
			const blockTaskItemDepth = $from.depth;
			const firstPosInBlockTaskItem = $from.start(blockTaskItemDepth);

			// Is the selection at the first possible position inside the blockTaskItem
			return $from.pos === firstPosInBlockTaskItem;
		}
	}

	return empty && ($from.parentOffset === 0 || state.selection instanceof GapCursorSelection);
};

export const isEmptySelectionAtEnd = (state: EditorState): boolean => {
	const { blockTaskItem } = state.schema.nodes;
	const { empty, $from } = state.selection;

	// If blockTaskItem is in the schema,
	// we need to check if the selection is inside a blockTaskItem
	if (blockTaskItem && empty) {
		// If the parent is in a textblock,
		// check if it's nested inside a blockTaskItem
		if ($from.parent.isTextblock) {
			const $posAtEndOfTextBlock = state.doc.resolve($from.end($from.depth - 1));
			const parentOfTextBlock = $posAtEndOfTextBlock.parent;

			// If the parent of the textblock is a blockTaskItem,
			// we need to know if the textblock is the last node in the blockTaskItem
			if (parentOfTextBlock.type === blockTaskItem) {
				const lastChildOfBlockTaskItem = parentOfTextBlock.lastChild;
				if (lastChildOfBlockTaskItem === $from.parent) {
					// If the textblock is the last node in the blockTaskItem,
					// check if the selection is at the end of the textblock
					const lastTextPosInTextBlock = $from.end($from.depth);
					return $from.pos === lastTextPosInTextBlock;
				} else {
					return false;
				}
			}
		}
		// Else, check if the parent is a blockTaskItem
		else if ($from.parent.type === blockTaskItem) {
			// Check if the selection is at the end of the blockTaskItem
			const lastPosInBlockTaskItem = $from.end();

			return $from.pos === lastPosInBlockTaskItem;
		}
	}

	return empty && ($from.end() === $from.pos || state.selection instanceof GapCursorSelection);
};

// eslint-disable-next-line @atlaskit/editor/no-re-export
export { filter as filterCommand };
// eslint-disable-next-line @atlaskit/editor/no-re-export
export { filter } from './filter';
// eslint-disable-next-line @atlaskit/editor/no-re-export
export { insertNewLine } from './insertNewLine';
// eslint-disable-next-line @atlaskit/editor/no-re-export
export { atTheEndOfDoc } from './atTheEndOfDoc';
// eslint-disable-next-line @atlaskit/editor/no-re-export
export { atTheBeginningOfDoc } from './atTheBeginningOfDoc';
// eslint-disable-next-line @atlaskit/editor/no-re-export
export { insertContentDeleteRange } from './insertContentDeleteRange';
